import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../environments/environment';

import { Intervento } from '../models/intervento.model';
import { TipoIntervento } from '../models/tipo-intervento.model';
import { RendicontaInterventoRequest } from '../models/requests/rendiconta-intervento.model';
import { DeterminaInterventoRequest } from '../models/requests/determina-intervento.model';
import { StoricoIntervento } from '../models/storico-intervento.model copy';
import { Rendicontazione } from '../models/rendicontazione.model';
import { StatoIntervento } from '../models/stato-intervento.model';
import { Liquidazione } from '../models/liquidazione.model';

@Injectable({
  providedIn: 'root'
})
export class InterventiService {
  private apiUrl = environment.apiUrl + "intervento"; // URL base per le API degli interventi
  private annoCorrente = new Date().getFullYear();

  constructor(
    private http: HttpClient,
    private logger: NGXLogger
  ) { }

  getInterventi(): Observable<Intervento[]> {
    const ruoloUtente = localStorage.getItem("ruolo");
   // this.logger.info('[INTERVENTI SERVICE] Ruolo utente', ruoloUtente);
    if (ruoloUtente === "ENTE_ESECUTORE") {
      const enteId = localStorage.getItem("enteId") || '';
      return this.http.get<Intervento[]>(`${this.apiUrl}?enteEsecutore=${enteId}`);
    }
    return this.http.get<Intervento[]>(`${this.apiUrl}`);
  }

  getStoricoIntervento(interventoId: number): Observable<StoricoIntervento[]> {
    return this.http.get<StoricoIntervento[]>(`${this.apiUrl}/${interventoId}/storico`);
  }

  getStoricoRendicontazioni(interventoId: number): Observable<Rendicontazione[]> {
    return this.http.get<Rendicontazione[]>(`${this.apiUrl}/${interventoId}/rendicontazioni`);
  }

  getStoricoLiquidazioni(interventoId: number): Observable<Liquidazione[]> {
    return this.http.get<Liquidazione[]>(`${this.apiUrl}/${interventoId}/liquidazioni`);
  }

  getInterventiComune(codiceCatastale: string): Observable<Intervento[]> {
    return this.http.get<Intervento[]>(`${this.apiUrl}?comune=${codiceCatastale}`);
  }

  getInterventiRichiesta( richiestaId: string): Observable<Intervento[]> {
    this.logger.info(`[INTERVENTI SERVICE] recupero gli interventi della richiesta ${richiestaId}`);
    return this.http.get<Intervento[]>(`${this.apiUrl}?richiestaId=${richiestaId}&showOther=true`);
  }
  getInterventiRichiestaPerVariante( richiestaId: string): Observable<Intervento[]> {
    this.logger.info(`[INTERVENTI SERVICE] recupero gli interventi della richiesta ${richiestaId}`);
    return this.http.get<Intervento[]>(`${this.apiUrl}?richiestaId=${richiestaId}&showOther=false`);
  }

  getInterventiConsorzio(consorzioId: string): Observable<Intervento[]> {
    return this.http.get<Intervento[]>(`${this.apiUrl}?enteEsecutore=${consorzioId}&showOther=false&anno=${this.annoCorrente}`);
  }

  getInterventiScheda(schedaId: string): Observable<Intervento[]> {
    return this.http.get<Intervento[]>(`${this.apiUrl}?scheda=${schedaId}`);
  }

  getTipiIntervento(): Observable<TipoIntervento[]> {
    return this.http.get<TipoIntervento[]>(`${this.apiUrl}/tipi`);
  }

  getStatiIntervento(): Observable<StatoIntervento[]> {
    return this.http.get<StatoIntervento[]>(`${this.apiUrl}/stati`);
  }

  crea(intervento: Intervento): Observable<Intervento> {
    return this.http.post<Intervento>(`${this.apiUrl}`, intervento);
  }

  riproponi(intervento: Intervento): Observable<Intervento> {
    return this.http.put<Intervento>(`${this.apiUrl}/${intervento.id}/riproponi`, intervento);
  }

  modifica(intervento: Intervento): Observable<Intervento> {
    this.logger.info('[INTERVENTI SERVICE] Modifica chiamando url ', `${this.apiUrl}/${intervento.id}`);
    return this.http.put<Intervento>(`${this.apiUrl}/${intervento.id}`, intervento);
  }

  rimodulaRichiesta(intervento: Intervento): Observable<Intervento> {
    return this.http.put<Intervento>(`${this.apiUrl}/${intervento.id}/rimodula`, intervento);
  }

  comunicaFinanziamento(intervento: Intervento): Observable<Intervento> {
    return this.http.put<Intervento>(`${this.apiUrl}/${intervento.id}/finanzia`, intervento);
  }

  rendiconta(intervento: Intervento, request: RendicontaInterventoRequest): Observable<Rendicontazione> {
    this.logger.info('[INTERVENTI SERVICE] Rendicontazione chiamando url ', `${this.apiUrl}/${intervento.id}/rendiconta`);
    this.logger.info('[INTERVENTI SERVICE] Rendicontazione request ', request);
    return this.http.put<Rendicontazione>(`${this.apiUrl}/${intervento.id}/rendiconta`, request);
  }
  comunicaDetermina(interventoId: number, rendicontazioneId: number, request: DeterminaInterventoRequest): Observable<Intervento> {
    return this.http.put<Intervento>(`${this.apiUrl}/${interventoId}/rendicontazioni/${rendicontazioneId}/determina`, request);
  }

  modificaRendicontazione(interventoId: number, rendicontazioneId: number, rendicontazione: Rendicontazione): Observable<Rendicontazione> {
    this.logger.info('[INTERVENTI SERVICE] Modifica rendicontazione chiamando url ', `${this.apiUrl}/${interventoId}/rendicontazioni/${rendicontazioneId}`);
    return this.http.put<Rendicontazione>(`${this.apiUrl}/${interventoId}/rendicontazioni/${rendicontazioneId}`, rendicontazione);
  }

  elimina(id: number | null): Observable<void> {
    if (!id) {
      this.logger.error('[INTERVENTI SERVICE] ID intervento non valido');
      return throwError(() => new Error('ID intervento non valido'));
    }
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        this.logger.error('[INTERVENTI] Errore durante l\'eliminazione dell\'intervento', error);
        return throwError(() => new Error('Errore durante l\'eliminazione dell\'intervento'));
      })
    );
  }

  eliminaRendicontazione(interventoId: number, rendicontazioneId: number): Observable<void> {
    if (!interventoId || !rendicontazioneId) {
      this.logger.error('[INTERVENTI SERVICE] Dati per eliminazione rendicontazione non validi', {
        interventoId,
        rendicontazioneId
      });
      return throwError(() => new Error('Dati per eliminazione rendicontazione non validi'));
    }

    const url = `${this.apiUrl}/${interventoId}/rendicontazioni/${rendicontazioneId}`;
    this.logger.info('[INTERVENTI SERVICE] Elimina rendicontazione chiamando url ', url);

    return this.http.delete<void>(url).pipe(
      catchError(error => {
        this.logger.error('[INTERVENTI] Errore durante l\'eliminazione della rendicontazione', error);
        return throwError(() => new Error('Errore durante l\'eliminazione della rendicontazione'));
      })
    );
  }

  getInterventiByRichiesta(richiestaId: number): Observable<Intervento[]> {
    return this.http.get<Intervento[]>(`${this.apiUrl}?richiestaId=${richiestaId}`);
  }

  getRendicontazioniByInterventoSenzaDetermina(interventoId: number): Observable<Intervento[]> {
    return this.http.get<Intervento[]>(`${this.apiUrl}/${interventoId}/rendicontazioni-senza-determina`);
  }


  assegnaDetermina(interventoId: number, numeroDetermina: string, note: string, fileUrls: string[]): Observable<Intervento> {
    const request: DeterminaInterventoRequest = {
      numeroDetermina,
      note,
      fileUrls
    };
    return this.http.put<Intervento>(`${this.apiUrl}/${interventoId}/determina`, request);
  }

  liquidaIntervento(
    interventoId: number,
    payload: { importo: number; dataLiquidazione: string; note: string }
  ): Observable<void> {
    const url = `${this.apiUrl}/${interventoId}/liquidazioni`;
    this.logger.info('[INTERVENTI SERVICE] Liquidazione intervento chiamando url ', url, 'payload', payload);
    return this.http.post<void>(url, payload).pipe(
      catchError(error => {
        this.logger.error('[INTERVENTI] Errore durante la liquidazione dell\'intervento', error);
        return throwError(() => new Error('Errore durante la liquidazione dell\'intervento'));
      })
    );
  }
}
